// Find the entry whose source edge sits at pos (or null)
const findEntry = (crossMap, source, edge, pos, { includeBond = true, includeSeparator = true } = {}) => {
    return crossMap.find(item =>
        item[source]?.[edge] === pos &&
        (includeBond || !item.bond) &&
        (includeSeparator || item.symbol !== "|")
    ) ?? null;
}

// Map a single pos from one coordinate space (from) to another (to)
const lookup = (crossMap, pos, { from, to, edge, includeBond = true, includeSeparator = true }) => {
    return findEntry(crossMap, from, edge, pos, { includeBond, includeSeparator })?.[to]?.[edge] ?? null;
}

// Map a {start, end} range from one space to another (null if either fails)
const lookupRange = (crossMap, { start, end }, { from, to, includeBond = true }) => {
    const s = lookup(crossMap, start, { from, to, edge: "start", includeBond });
    const e = lookup(crossMap, end,   { from, to, edge: "end",   includeBond });
    return (s === null || e === null) ? null : { start: s, end: e };
}

// Like lookupRange, but handle a collapsed selection (start === end) with end-of-map fallback
const lookupSelection = (crossMap, { start, end }, { from, to, includeBond = true }) => {
    if (start === end) {
        const pos = lookup(crossMap, start, { from, to, edge: "start", includeBond })
            ?? crossMap[crossMap.length - 1]?.[to]?.start
            ?? null;
        return pos === null ? null : { start: pos, end: pos };
    }
    return lookupRange(crossMap, { start, end }, { from, to, includeBond });
}

// Find the entry whose source range exactly matches {start, end} (or null)
const findEntryByRange = (crossMap, { start, end }, source, includeBond = true) => {
    return crossMap.find(item =>
        item[source]?.start === start &&
        item[source]?.end === end &&
        (includeBond || !item.bond)
    ) ?? null;
}

// Find a real encoding (or downstream linkage) whose source edge is at pos
const findEncodingByBoundary = (crossMap, source, pos, edge, { includeSeparator = true, includeDownstreamLinkage = false } = {}) => {
    const isDownstreamLinkage = item => item.pk < 0 && item.symbol === null;

    const matches = item =>
        (includeSeparator || item.symbol !== "|") &&
        ((includeDownstreamLinkage && isDownstreamLinkage(item)) ||
            item[source]?.start !== item[source]?.end);

    return crossMap.find(item =>
        matches(item) && item[source]?.[edge] === pos
    ) ?? null;
};

// Find the first or last real encoding (last=true → last)
const findEncoding = (crossMap, source, { includeSeparator = false, last = false } = {}) => {
    const matches = item =>
        (includeSeparator || item.symbol !== "|") &&
        item[source]?.start !== item[source]?.end;
    return crossMap[last ? "findLast" : "find"](matches) ?? null;
};

export {
    findEntry,
    lookup,
    lookupRange,
    lookupSelection,
    findEntryByRange,
    findEncodingByBoundary,
    findEncoding
}